import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import "./DocumentRegistryDashboard.css";

import { useNavigate } from "react-router-dom";

import axiosInstance from "../../../Config/axiosInstance";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ApartmentIcon from "@mui/icons-material/Apartment";

const DocumentRegistryDashboard = ({
    open = true,
}) => {
    const navigate = useNavigate();

    const [organizations, setOrganizations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchOrganizations = useCallback(async () => {
        setIsLoading(true);
        setError("");

        try {
            const response = await axiosInstance.get(
                "/organizations/"
            );

            const organizationsData = Array.isArray(
                response.data
            )
                ? response.data
                : response.data?.results || [];

            const activeOrganizations =
                organizationsData
                    .filter(
                        (organization) =>
                            organization.is_active !== false
                    )
                    .sort((first, second) =>
                        String(
                            first.name || ""
                        ).localeCompare(
                            String(second.name || ""),
                            "en",
                            {
                                sensitivity: "base",
                            }
                        )
                    );

            setOrganizations(activeOrganizations);
        } catch (requestError) {
            console.error(
                "Error loading organizations:",
                requestError
            );

            setOrganizations([]);

            setError(
                requestError?.response?.data?.detail ||
                requestError?.response?.data?.message ||
                "Failed to load organizations."
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        fetchOrganizations();
    }, [open, fetchOrganizations]);

    const handleClose = () => {
        navigate("/home");
    };

    const handleOrganizationClick = (
        organization
    ) => {
        if (
            organization?.id === null ||
            organization?.id === undefined
        ) {
            return;
        }

        navigate(
            `/document-registry?organization=${organization.id}`
        );
    };

    const handleLogoError = (event) => {
        event.currentTarget.style.display = "none";

        const fallback =
            event.currentTarget.nextElementSibling;

        if (fallback) {
            fallback.style.display = "flex";
        }
    };

    return (
        <Dialog
            open={Boolean(open)}
            onClose={handleClose}
            fullWidth
            maxWidth={false}
            className="document-registry-dashboard-dialog"
            aria-labelledby="document-registry-dashboard-title"
            PaperProps={{
                className:
                    "document-registry-dashboard-dialog-paper",
            }}
        >
            <DialogTitle
                id="document-registry-dashboard-title"
                className="document-registry-dashboard-dialog-title"
            >
                <ApartmentIcon />
                Select Company
            </DialogTitle>

            <DialogContent className="document-registry-dashboard-dialog-content">
                {isLoading && (
                    <div className="document-registry-dashboard-state">
                        <CircularProgress
                            size={40}
                            thickness={4}
                        />

                        <span className="document-registry-dashboard-state-title">
                            Loading organizations...
                        </span>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="document-registry-dashboard-state">
                        <span className="document-registry-dashboard-state-icon document-registry-dashboard-state-icon-error">
                            <BusinessOutlinedIcon />
                        </span>

                        <span className="document-registry-dashboard-state-title">
                            Failed to load organizations
                        </span>

                        <span className="document-registry-dashboard-state-description">
                            {error}
                        </span>

                        <button
                            type="button"
                            className="document-registry-dashboard-retry-button"
                            onClick={fetchOrganizations}
                        >
                            <RefreshRoundedIcon />
                            Retry
                        </button>
                    </div>
                )}

                {!isLoading &&
                    !error &&
                    organizations.length === 0 && (
                        <div className="document-registry-dashboard-state">
                            <span className="document-registry-dashboard-state-icon">
                                <BusinessOutlinedIcon />
                            </span>

                            <span className="document-registry-dashboard-state-title">
                                No active organizations found
                            </span>

                            <span className="document-registry-dashboard-state-description">
                                Contact your administrator to
                                add organizations.
                            </span>
                        </div>
                    )}

                {!isLoading &&
                    !error &&
                    organizations.length > 0 && (
                        <div className="document-registry-dashboard-organization-list">
                            {organizations.map(
                                (organization) => (
                                    <button
                                        type="button"
                                        key={organization.id}
                                        className="document-registry-dashboard-organization-row"
                                        onClick={() =>
                                            handleOrganizationClick(
                                                organization
                                            )
                                        }
                                    >
                                        <span className="document-registry-dashboard-organization-logo">
                                            {organization.logo && (
                                                <img
                                                    src={
                                                        organization.logo
                                                    }
                                                    alt={`${organization.name ||
                                                        "Organization"
                                                        } logo`}
                                                    loading="lazy"
                                                    onError={
                                                        handleLogoError
                                                    }
                                                />
                                            )}

                                            <span
                                                className="document-registry-dashboard-organization-logo-fallback"
                                                style={{
                                                    display:
                                                        organization.logo
                                                            ? "none"
                                                            : "flex",
                                                }}
                                            >
                                                <BusinessOutlinedIcon />
                                            </span>
                                        </span>

                                        <span className="document-registry-dashboard-organization-information">
                                            <span className="document-registry-dashboard-organization-name">
                                                {organization.name ||
                                                    "Unnamed Organization"}
                                            </span>

                                            {organization.cui && (
                                                <span className="document-registry-dashboard-organization-cui">
                                                    Registration No.:{" "}
                                                    {
                                                        organization.cui
                                                    }
                                                </span>
                                            )}
                                        </span>

                                        <span className="document-registry-dashboard-organization-arrow">
                                            <ArrowForwardIosRoundedIcon />
                                        </span>
                                    </button>
                                )
                            )}
                        </div>
                    )}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentRegistryDashboard;